import * as THREE from 'three';
import type { SketchFaceGeometryFrame, SketchFaceViewFrame, SketchFaceViewHint, SketchPlanarFaceSelection } from './SketchPlanarFaceSelection';

/**
 * 面草图视图帧构造器。
 *
 * 设计说明：
 * 1. 该构造器统一负责“从当前相机进入面法向视图”的姿态计算；
 * 2. 进入前后必须保持位于几何法向同侧，避免草图进入后无故翻面；
 * 3. 相机 up 与草图坐标系必须由稳定世界轴投影得到，不能继承旧相机旋转；
 * 4. 同一份结果同时服务临时草图平面坐标系与相机视图切换。
 */
export class SketchFaceViewFrameBuilder {
    /** 数值容差。 */
    private static readonly EPSILON = 1e-8;
    /** 默认拟合边距。 */
    private static readonly DEFAULT_PADDING = 1.25;
    /** 世界 X 轴。 */
    private static readonly WORLD_X = new THREE.Vector3(1, 0, 0);
    /** 世界 Y 轴。 */
    private static readonly WORLD_Y = new THREE.Vector3(0, 1, 0);

    /**
     * 构造视图帧。
     *
     * @param input 输入参数。
     * @returns 视图帧。
     */
    public build(input: {
        face: SketchPlanarFaceSelection;
        camera: THREE.Camera;
        viewportAspect: number;
        padding?: number;
    }): SketchFaceViewFrame {
        const { face, camera, viewportAspect } = input;
        const padding = input.padding ?? SketchFaceViewFrameBuilder.DEFAULT_PADDING;

        const geometricNormal = face.worldPlaneNormal.clone().normalize();
        const selectionPoint = face.worldRepresentativePoint.clone();
        const toCamera = camera.position.clone().sub(selectionPoint).normalize();
        const displayNormal = geometricNormal.dot(toCamera) >= 0
            ? geometricNormal.clone()
            : geometricNormal.clone().negate();
        const viewForward = displayNormal.clone().negate();

        const geometryFrame = this.resolveGeometryFrame(face);
        const target = face.worldCentroid.clone();
        const up = this.resolveStableUp(displayNormal);
        const distance = this.fitDistanceToVertices(
            face.worldVertices,
            target,
            up,
            displayNormal,
            this.resolveCameraFov(camera),
            viewportAspect,
            padding,
        );
        const cameraPosition = target.clone().sub(viewForward.clone().multiplyScalar(distance));
        const viewHint: SketchFaceViewHint = {
            lastDisplayNormal: displayNormal.clone(),
        };

        return {
            target,
            cameraPosition,
            up,
            distance,
            displayNormal,
            viewForward,
            sideBefore: this.signNonZero(geometricNormal.dot(camera.position.clone().sub(target))),
            sideAfter: this.signNonZero(geometricNormal.dot(cameraPosition.clone().sub(target))),
            geometryFrame,
            viewHint,
        };
    }

    /**
     * 求非零符号。
     *
     * @param value 输入值。
     * @returns -1 或 1。
     */
    private signNonZero(value: number): -1 | 1 {
        return value >= 0 ? 1 : -1;
    }

    /**
     * 将向量投影到平面内。
     *
     * @param vector 输入向量。
     * @param planeNormal 平面法向。
     * @returns 归一化后的投影向量。
     */
    private projectVectorOnPlane(vector: THREE.Vector3, planeNormal: THREE.Vector3): THREE.Vector3 {
        const projected = vector
            .clone()
            .sub(planeNormal.clone().multiplyScalar(vector.dot(planeNormal)));
        if (projected.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            return new THREE.Vector3(0, 0, 0);
        }
        return projected.normalize();
    }

    /**
     * 解析稳定的视图向上方向。
     *
     * @param displayNormal 当前显示法向。
     * @returns 平面内稳定 up。
     */
    private resolveStableUp(displayNormal: THREE.Vector3): THREE.Vector3 {
        let up = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_Y, displayNormal);
        if (up.lengthSq() > SketchFaceViewFrameBuilder.EPSILON) {
            return up;
        }

        up = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_X, displayNormal);
        if (up.lengthSq() > SketchFaceViewFrameBuilder.EPSILON) {
            return up;
        }

        return new THREE.Vector3(0, 1, 0);
    }

    /**
     * 解析草图几何坐标系。
     *
     * @param face 面快照。
     * @returns 几何坐标系。
     */
    private resolveGeometryFrame(face: SketchPlanarFaceSelection): SketchFaceGeometryFrame {
        const geometricNormal = face.worldPlaneNormal.clone().normalize();
        const origin = face.worldRepresentativePoint.clone();

        /**
         * 特征面草图坐标系必须稳定、可复现：
         * 旧相机 up 只表达用户进入前的观察旋转，不是草图平面的设计意图。
         * 因此这里与默认基准面一致，优先使用世界 Y 投影，退化时再回退世界 X。
         */
        let yAxis = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_Y, geometricNormal);
        if (yAxis.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            yAxis = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_X, geometricNormal);
        }
        if (yAxis.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            yAxis = new THREE.Vector3(0, 1, 0);
        }

        let xAxis = yAxis.clone().cross(geometricNormal).normalize();
        if (xAxis.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            xAxis = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_X, geometricNormal);
        }
        if (xAxis.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            xAxis = new THREE.Vector3(1, 0, 0);
        }

        yAxis = geometricNormal.clone().cross(xAxis).normalize();
        return {
            origin,
            xAxis,
            yAxis,
            geometricNormal,
        };
    }

    /**
     * 基于面顶点求视图拟合距离。
     *
     * @param vertices 面顶点。
     * @param target 观察目标。
     * @param up 相机 up。
     * @param displayNormal 显示法向。
     * @param fovDeg 视角。
     * @param aspect 视口宽高比。
     * @param padding 边距。
     * @returns 拟合距离。
     */
    private fitDistanceToVertices(
        vertices: THREE.Vector3[],
        target: THREE.Vector3,
        up: THREE.Vector3,
        displayNormal: THREE.Vector3,
        fovDeg: number,
        aspect: number,
        padding: number,
    ): number {
        const validAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
        let right = new THREE.Vector3().crossVectors(up, displayNormal);
        if (right.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            right = this.projectVectorOnPlane(SketchFaceViewFrameBuilder.WORLD_X, displayNormal);
        }
        if (right.lengthSq() <= SketchFaceViewFrameBuilder.EPSILON) {
            right = new THREE.Vector3(1, 0, 0);
        }
        right.normalize();

        let halfWidth = 0;
        let halfHeight = 0;
        vertices.forEach((vertex) => {
            const delta = vertex.clone().sub(target);
            halfWidth = Math.max(halfWidth, Math.abs(delta.dot(right)));
            halfHeight = Math.max(halfHeight, Math.abs(delta.dot(up)));
        });

        if (halfWidth <= SketchFaceViewFrameBuilder.EPSILON && halfHeight <= SketchFaceViewFrameBuilder.EPSILON) {
            return 20;
        }

        const verticalFov = THREE.MathUtils.degToRad(Math.max(1, fovDeg));
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * validAspect);
        const distanceByHeight = halfHeight / Math.max(Math.tan(verticalFov / 2), 1e-4);
        const distanceByWidth = halfWidth / Math.max(Math.tan(horizontalFov / 2), 1e-4);
        return Math.max(distanceByHeight, distanceByWidth) * padding;
    }

    /**
     * 解析相机视角。
     *
     * @param camera 相机。
     * @returns 视角角度。
     */
    private resolveCameraFov(camera: THREE.Camera): number {
        if (camera instanceof THREE.PerspectiveCamera) {
            return camera.fov;
        }
        return 45;
    }
}
